/** One row from `/json-data/global_invoiced_trends.json` (may repeat per year-month). */
export interface GlobalInvoicedTrendRow {
  year?: number;
  month?: number;
  global_invoiced_usd?: string | number;
}

/**
 * Aggregate by calendar month, then sum invoiced USD for the **latest** 12 distinct months.
 */
export function trailing12MonthInvoicedUsd(rows: GlobalInvoicedTrendRow[]): number {
  const byMonthKey = new Map<number, number>();
  for (const r of rows) {
    const y = Number(r.year);
    const m = Number(r.month);
    if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) continue;
    const key = y * 100 + m;
    const v = Number(r.global_invoiced_usd) || 0;
    byMonthKey.set(key, (byMonthKey.get(key) || 0) + v);
  }
  const keys = [...byMonthKey.keys()].sort((a, b) => b - a);
  let sum = 0;
  for (let i = 0; i < Math.min(12, keys.length); i++) {
    sum += byMonthKey.get(keys[i]) ?? 0;
  }
  return sum;
}

/**
 * Classic DSO: AR ÷ (trailing-12-month credit sales) × 365.
 * Returns null if T12 sales ≤ 0 or inputs invalid.
 */
export function computeDsoDays(arUsd: number, t12InvoicedUsd: number): number | null {
  if (!Number.isFinite(arUsd) || arUsd < 0) return null;
  if (!Number.isFinite(t12InvoicedUsd) || t12InvoicedUsd <= 0) return null;
  return (arUsd / t12InvoicedUsd) * 365;
}
